/**
 * Audio Store
 *
 * Zustand store wrapping the AudioEngine singleton.
 * Tracks current mood, active ambience layers, and volume levels.
 * GM controls what plays; commands broadcast to all players via Supabase realtime.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { audioEngine, type BusName } from '../audio/audioEngine';
import {
  AMBIENCE_LAYERS,
  SFX_ENTRIES,
  moodPlaylist,
  type MusicTrack,
} from '../audio/trackManifest';
import { drawFromShuffleBag } from '../audio/playlist';
import {
  anchoredPosition,
  pushHistory,
  removeFromBag,
  resolvePrevious,
  shouldAdvance,
  type MusicAnchor,
} from '../audio/transport';
import { supabase } from '../lib/supabase';
import { DEFAULT_NARRATOR_VOICE_ID } from '@machina/core';
import type { RealtimeChannel } from '@supabase/supabase-js';

// =============================================================================
// AUDIO COMMAND TYPES (broadcast payload)
// =============================================================================

export interface AudioCommand {
  type:
    | 'mood' | 'layer-on' | 'layer-off' | 'layer-vol' | 'sfx' | 'bus-vol' | 'full-sync'
    // Music transport (ISS-0186). Clients older than this ignore them.
    | 'music-pause' | 'music-resume' | 'music-seek'
    // A client that just (re)joined asks the GM for a full-sync.
    | 'sync-request';
  /** sync-request: the asking client, so it can ignore its own echo. */
  from?: string;
  moodId?: string | null;
  /**
   * The concrete track the GM is playing ('mood' and 'full-sync'). Sent whole,
   * not as an id, so generated and BYO tracks that players' manifests do not
   * know still reach them. Absent from clients older than ISS-0183.
   */
  track?: MusicTrack;
  /**
   * Seconds into the track: where to pause, resume or seek to, or where a
   * track or full-sync starts. Absent means "from wherever you are" (or 0 for
   * a new track).
   */
  position?: number;
  /** full-sync / 'mood': the GM's music is paused at `position`. */
  paused?: boolean;
  layerId?: string;
  sfxId?: string;
  bus?: BusName;
  volume?: number;
  // full-sync payload
  activeLayers?: string[];
  layerVolumes?: Record<string, number>;
  volumes?: { master: number; music: number; ambience: number; sfx: number; voice?: number };
}

// =============================================================================
// STATE
// =============================================================================

interface AudioState {
  isInitialized: boolean;
  isMuted: boolean;             // player's personal mute toggle (persisted)
  currentMood: string | null;   // mood ID
  /** The track actually playing, which may not be in the manifest. */
  currentTrack: MusicTrack | null;
  /** Music is paused, holding its position (Stop clears the track instead). */
  musicPaused: boolean;
  /** GM: pin the current track so it loops instead of advancing. */
  repeatOne: boolean;
  /**
   * Player whose sound is not enabled yet: where the GM's track was, and
   * when. init() starts the track from here, plus the time since.
   */
  musicAnchor: MusicAnchor | null;
  activeLayers: string[];       // ambience layer IDs
  layerVolumes: Record<string, number>; // per-layer volume overrides
  diceSoundsEnabled: boolean;   // play sounds on dice rolls
  /**
   * Read the GM's narration aloud (EPIC-0043 spike, Kokoro in-browser).
   * Defaults OFF: voice is a preference, the model is an ~80MB download, and
   * neither should happen to a player who did not ask for them.
   */
  voiceEnabled: boolean;
  /**
   * True while the Narrator is actually speaking. Drives the header transport,
   * which is otherwise a plain mute button — there is nothing to control until
   * something is talking.
   */
  isSpeaking: boolean;
  /** Last narration spoken, so it can be replayed without another GM turn. */
  lastSpokenText: string | null;
  /**
   * Chosen NarratorVoice id (see NARRATOR_VOICES in @machina/core). Stored as
   * an id rather than a provider voice name so a voice can be re-pointed at a
   * different provider without invalidating saved preferences.
   */
  voiceId: string;
  volumes: {
    master: number;
    music: number;
    ambience: number;
    sfx: number;
    voice: number;
  };

  // Broadcast state
  broadcastChannel: RealtimeChannel | null;
  isBroadcasting: boolean; // true = GM sending commands

  // Actions
  init: () => void;
  setMuted: (muted: boolean) => void;
  setMood: (moodId: string | null) => void;
  /** GM: play a specific track. Accepts tracks the manifest does not list. */
  playTrack: (track: MusicTrack) => void;
  /** GM: skip to the next track in the current mood's playlist. */
  nextTrack: () => void;
  /** GM: restart the track, or go back one in this mood's history. */
  previousTrack: () => void;
  /** GM: play a track from the current mood's list now, with the crossfade. */
  playFromList: (trackId: string) => void;
  pauseMusic: () => void;
  resumeMusic: () => void;
  seekMusic: (seconds: number) => void;
  setRepeatOne: (on: boolean) => void;
  toggleLayer: (layerId: string) => void;
  setLayerVolume: (layerId: string, volume: number) => void;
  triggerSFX: (sfxId: string) => void;
  setVolume: (bus: BusName, volume: number) => void;
  setDiceSoundsEnabled: (enabled: boolean) => void;
  setVoiceEnabled: (enabled: boolean) => void;
  setVoiceId: (voiceId: string) => void;
  setSpeaking: (speaking: boolean, text?: string) => void;
  playDiceSound: () => void;

  // Broadcast
  startBroadcasting: (campaignId: string) => void;
  stopBroadcasting: () => void;
  subscribeToAudio: (campaignId: string) => void;
  unsubscribeFromAudio: () => void;
}

export const useAudioStore = create<AudioState>()(
  persist(
  (set, get) => ({
  isInitialized: false,
  isMuted: false,
  currentMood: null,
  currentTrack: null,
  musicPaused: false,
  repeatOne: false,
  musicAnchor: null,
  activeLayers: [],
  layerVolumes: {},
  diceSoundsEnabled: true,
  voiceEnabled: false,
  isSpeaking: false,
  lastSpokenText: null,
  voiceId: DEFAULT_NARRATOR_VOICE_ID,
  volumes: {
    master: 0.7,
    music: 0.5,
    ambience: 0.6,
    sfx: 0.8,
    voice: 1,
  },
  broadcastChannel: null,
  isBroadcasting: false,

  init: () => {
    if (get().isInitialized) return;

    audioEngine.init();

    // Apply stored volumes (respect mute state)
    const { volumes, isMuted } = get();
    audioEngine.setVolume('master', isMuted ? 0 : volumes.master);
    audioEngine.setVolume('music', volumes.music);
    audioEngine.setVolume('ambience', volumes.ambience);
    audioEngine.setVolume('sfx', volumes.sfx);

    set({ isInitialized: true });

    // A player who heard about the GM's audio before enabling sound starts
    // where the table is now, not where it was when the sync arrived.
    const { currentTrack, musicPaused, musicAnchor, activeLayers, layerVolumes } = get();
    if (currentTrack) {
      void audioEngine.playMusic(currentTrack.url, {
        loop: true,
        startAt: anchoredPosition(musicAnchor, musicPaused, Date.now(), currentTrack.durationSec),
        startPaused: musicPaused,
      });
    }
    for (const layerId of activeLayers) {
      const layer = AMBIENCE_LAYERS.find((l) => l.id === layerId);
      if (layer) void audioEngine.addAmbienceLayer(layerId, layer.url, layerVolumes[layerId] ?? 0.6);
    }
    set({ musicAnchor: null });
  },

  setMuted: (muted) => {
    set({ isMuted: muted });
    if (get().isInitialized) {
      audioEngine.setVolume('master', muted ? 0 : get().volumes.master);
    }
  },

  // ===========================================================================
  // MOOD
  // ===========================================================================

  // The GM's client owns the playlist: it picks each track, advances when one
  // is about to end, and broadcasts the pick. Players only ever play what they
  // are told (see handleRemoteCommand).

  setMood: (moodId) => {
    const { isInitialized, currentMood, currentTrack } = get();
    if (!isInitialized) return;
    if (moodId === currentMood) return;

    // A pin belongs to the track it was set on, not to the next mood.
    if (moodId === null) {
      audioEngine.stopMusic();
      set({ currentMood: null, currentTrack: null, musicPaused: false, repeatOne: false });
      broadcast(get(), { type: 'mood', moodId: null });
      return;
    }

    const track = drawNextTrack(moodId, currentTrack?.id ?? null);
    if (!track) return;
    set({ repeatOne: false });
    get().playTrack(track);
  },

  playTrack: (track) => {
    if (!get().isInitialized) return;
    startGmTrack(track, 2);
    broadcast(get(), { type: 'mood', moodId: track.moodId, track });
  },

  nextTrack: () => {
    const { isInitialized, currentMood, currentTrack } = get();
    if (!isInitialized || !currentMood) return;
    if (moodPlaylist(currentMood).length < 2) return;

    const track = drawNextTrack(currentMood, currentTrack?.id ?? null);
    if (!track) return;
    startGmTrack(track, 2);
    broadcast(get(), { type: 'mood', moodId: currentMood, track });
  },

  previousTrack: () => {
    const { isInitialized, currentMood, currentTrack } = get();
    if (!isInitialized || !currentMood || !currentTrack) return;

    const position = audioEngine.getMusicState()?.currentTime ?? 0;
    const action = resolvePrevious(playHistory.get(currentMood) ?? [], currentTrack.id, position);
    const previous = action.kind === 'track'
      ? moodPlaylist(currentMood).find((t) => t.id === action.trackId)
      : undefined;

    if (action.kind === 'track' && previous) {
      playHistory.set(currentMood, action.history);
      startGmTrack(previous, 2, { recordHistory: false });
      broadcast(get(), { type: 'mood', moodId: currentMood, track: previous });
    } else {
      get().seekMusic(0);
    }
  },

  playFromList: (trackId) => {
    const { isInitialized, currentMood, currentTrack } = get();
    if (!isInitialized || !currentMood || currentTrack?.id === trackId) return;
    const track = moodPlaylist(currentMood).find((t) => t.id === trackId);
    if (!track) return;

    shuffleBags.set(currentMood, removeFromBag(shuffleBags.get(currentMood) ?? [], trackId));
    get().playTrack(track);
  },

  pauseMusic: () => {
    const { isInitialized, currentTrack, musicPaused } = get();
    if (!isInitialized || !currentTrack || musicPaused) return;
    const position = audioEngine.getMusicState()?.currentTime ?? 0;
    audioEngine.pauseMusic();
    set({ musicPaused: true });
    broadcast(get(), { type: 'music-pause', position });
  },

  resumeMusic: () => {
    const { isInitialized, currentTrack, musicPaused } = get();
    if (!isInitialized || !currentTrack || !musicPaused) return;
    const position = audioEngine.getMusicState()?.currentTime ?? 0;
    audioEngine.resumeMusic();
    set({ musicPaused: false });
    broadcast(get(), { type: 'music-resume', position });
  },

  seekMusic: (seconds) => {
    const { isInitialized, currentTrack } = get();
    if (!isInitialized || !currentTrack || !Number.isFinite(seconds)) return;
    audioEngine.seekMusic(seconds);
    broadcast(get(), { type: 'music-seek', position: Math.max(0, seconds) });
  },

  // Repeat-one is GM policy only: players already loop while they wait for
  // the next pick, so pinning needs no command of its own.
  setRepeatOne: (on) => set({ repeatOne: on }),

  // ===========================================================================
  // AMBIENCE LAYERS
  // ===========================================================================

  toggleLayer: (layerId) => {
    const { isInitialized, activeLayers, layerVolumes } = get();
    if (!isInitialized) return;

    if (activeLayers.includes(layerId)) {
      audioEngine.removeAmbienceLayer(layerId);
      set({ activeLayers: activeLayers.filter((id) => id !== layerId) });
      broadcast(get(), { type: 'layer-off', layerId });
    } else {
      const layer = AMBIENCE_LAYERS.find((l) => l.id === layerId);
      if (!layer) return;
      const vol = layerVolumes[layerId] ?? 0.6;
      audioEngine.addAmbienceLayer(layerId, layer.url, vol);
      set({ activeLayers: [...activeLayers, layerId] });
      broadcast(get(), { type: 'layer-on', layerId, volume: vol });
    }
  },

  setLayerVolume: (layerId, volume) => {
    const { layerVolumes } = get();
    audioEngine.setAmbienceLayerVolume(layerId, volume);
    set({ layerVolumes: { ...layerVolumes, [layerId]: volume } });
    broadcast(get(), { type: 'layer-vol', layerId, volume });
  },

  // ===========================================================================
  // SFX
  // ===========================================================================

  triggerSFX: (sfxId) => {
    const { isInitialized } = get();
    if (!isInitialized) return;

    const entry = SFX_ENTRIES.find((s) => s.id === sfxId);
    if (!entry) return;

    audioEngine.playSFX(entry.url);
    broadcast(get(), { type: 'sfx', sfxId });
  },

  // ===========================================================================
  // VOLUME
  // ===========================================================================

  setVolume: (bus, volume) => {
    // If setting master and muted, store the value but keep engine at 0
    if (bus === 'master' && get().isMuted) {
      set({ volumes: { ...get().volumes, master: volume } });
      return;
    }
    audioEngine.setVolume(bus, volume);
    set({ volumes: { ...get().volumes, [bus]: volume } });
    // Don't broadcast individual volume — player controls their own
  },

  // ===========================================================================
  // DICE SOUNDS (local only, not broadcast)
  // ===========================================================================

  setDiceSoundsEnabled: (enabled) => {
    set({ diceSoundsEnabled: enabled });
  },

  setSpeaking: (speaking, text) => {
    set(text !== undefined ? { isSpeaking: speaking, lastSpokenText: text } : { isSpeaking: speaking });
  },

  setVoiceId: (voiceId) => {
    set({ voiceId });
    // Stop any line already playing: it is in the OLD voice, and hearing the
    // previous narrator finish a sentence after switching reads as a bug.
    void import('../audio/voice').then(({ stopSpeaking }) => stopSpeaking());
  },

  setVoiceEnabled: (enabled) => {
    set({ voiceEnabled: enabled });
    // Turning it off mid-sentence must actually stop the sentence, not wait
    // for it to finish.
    if (!enabled) {
      void import('../audio/voice').then(({ stopSpeaking }) => stopSpeaking());
    }
  },

  playDiceSound: () => {
    const { isInitialized, diceSoundsEnabled } = get();
    if (!isInitialized || !diceSoundsEnabled) return;
    audioEngine.playSFX('/audio/sfx/dice-roll.ogg');
  },

  // ===========================================================================
  // BROADCAST (GM side — sends commands to players)
  // ===========================================================================

  startBroadcasting: (campaignId: string) => {
    if (get().isBroadcasting) return; // already broadcasting

    const { broadcastChannel } = get();
    if (broadcastChannel) {
      // Channel already exists from subscribeToAudio — just enable sending
      console.log('[audio] broadcasting on existing channel', `audio-${campaignId}`);
      set({ isBroadcasting: true });
      return;
    }

    // Listen as well as send: joining players ask the GM for a full-sync.
    const channel = supabase
      .channel(`audio-${campaignId}`)
      .on('broadcast', { event: 'audio-cmd' }, (payload) => {
        handleRemoteCommand(payload.payload as AudioCommand);
      });
    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log('[audio] broadcasting on', `audio-${campaignId}`);
        set({ isBroadcasting: true });
      }
    });

    set({ broadcastChannel: channel });
  },

  stopBroadcasting: () => {
    const { broadcastChannel } = get();
    if (broadcastChannel) {
      supabase.removeChannel(broadcastChannel);
      set({ broadcastChannel: null, isBroadcasting: false });
    }
  },

  // ===========================================================================
  // SUBSCRIBE (player side — receives commands from GM)
  // ===========================================================================

  subscribeToAudio: (campaignId: string) => {
    get().unsubscribeFromAudio();

    const channel = supabase
      .channel(`audio-${campaignId}`)
      .on('broadcast', { event: 'audio-cmd' }, (payload) => {
        const cmd = payload.payload as AudioCommand;
        handleRemoteCommand(cmd);
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('[audio] subscribed to', `audio-${campaignId}`);
          // Fires again after a reconnect, which is when a player has most
          // likely missed commands. The GM (broadcasting) has nothing to ask.
          if (!get().isBroadcasting) {
            channel.send({
              type: 'broadcast',
              event: 'audio-cmd',
              payload: { type: 'sync-request', from: AUDIO_CLIENT_ID } satisfies AudioCommand,
            });
          }
        }
      });

    set({ broadcastChannel: channel });
  },

  unsubscribeFromAudio: () => {
    const { broadcastChannel } = get();
    if (broadcastChannel) {
      supabase.removeChannel(broadcastChannel);
      set({ broadcastChannel: null, isBroadcasting: false });
    }
  },
}),
  {
    name: 'machina-audio',
    partialize: (state) => ({
      volumes: state.volumes,
      diceSoundsEnabled: state.diceSoundsEnabled,
      voiceEnabled: state.voiceEnabled,
      voiceId: state.voiceId,
      isMuted: state.isMuted,
    }),
  }
));

// =============================================================================
// HELPERS
// =============================================================================

/** Seconds of crossfade into the next track when one ends on its own. */
const TRACK_END_CROSSFADE = 4;

/** Per-mood shuffle bags. Session-only: a fresh shuffle on reload is fine. */
const shuffleBags = new Map<string, string[]>();

/** Per-mood play history (GM), newest last, for Previous. Session-only. */
const playHistory = new Map<string, string[]>();

/** A player's copy is "in sync" within this many seconds of the GM's. */
const DRIFT_TOLERANCE = 2;

/** Identifies this tab's sync-requests, so it can ignore its own echo. */
export const AUDIO_CLIENT_ID =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

/**
 * The GM answers sync-requests at most once per window: a burst of joins (a
 * whole table arriving, or everyone reconnecting after a blip) gets one reply
 * now and, if more asked meanwhile, one more at the end of the window.
 */
export const SYNC_REPLY_WINDOW_MS = 2000;
let lastSyncReplyAt = -Infinity;
let syncReplyTimer: ReturnType<typeof setTimeout> | null = null;

function requestSyncReply() {
  if (syncReplyTimer) return;
  const wait = lastSyncReplyAt + SYNC_REPLY_WINDOW_MS - Date.now();
  if (wait <= 0) {
    sendFullSync();
    return;
  }
  syncReplyTimer = setTimeout(() => {
    syncReplyTimer = null;
    sendFullSync();
  }, wait);
}

function sendFullSync() {
  lastSyncReplyAt = Date.now();
  const state = useAudioStore.getState();
  const music = audioEngine.getMusicState();
  broadcast(state, {
    type: 'full-sync',
    moodId: state.currentMood,
    track: state.currentTrack ?? undefined,
    position: music?.currentTime ?? 0,
    paused: state.musicPaused,
    activeLayers: state.activeLayers,
    layerVolumes: state.layerVolumes,
  });
}

/** Test hook: forget the throttle between tests. */
export function resetSyncReplyThrottle() {
  lastSyncReplyAt = -Infinity;
  if (syncReplyTimer) clearTimeout(syncReplyTimer);
  syncReplyTimer = null;
}

/**
 * Whether the page has had a user gesture, so an AudioContext created now
 * will run. Created before one, it starts suspended and stays silent.
 */
function hasUserActivation(): boolean {
  if (typeof navigator === 'undefined') return false;
  const activation = (navigator as Navigator & { userActivation?: { hasBeenActive: boolean } }).userActivation;
  return activation?.hasBeenActive === true;
}

/**
 * A command for a player whose sound is not enabled yet: keep the state it
 * describes in the store (what plays, where, paused or not, which layers) so
 * init() can start it all at the right spot. One-shot SFX are dropped.
 */
function rememberCommand(cmd: AudioCommand) {
  const state = useAudioStore.getState();
  const now = Date.now();
  const here = () =>
    anchoredPosition(state.musicAnchor, state.musicPaused, now, state.currentTrack?.durationSec);
  const at = (position: number | undefined, fallback: () => number): MusicAnchor => ({
    position: position !== undefined && Number.isFinite(position) ? position : fallback(),
    at: now,
  });

  switch (cmd.type) {
    case 'mood':
    case 'full-sync': {
      const track = trackFromCommand(cmd);
      if (track === null) {
        useAudioStore.setState({ currentMood: null, currentTrack: null, musicPaused: false, musicAnchor: null });
      } else if (track) {
        const same = state.currentTrack?.id === track.id && state.currentTrack.url === track.url;
        useAudioStore.setState({
          currentMood: track.moodId,
          currentTrack: track,
          musicPaused: cmd.paused ?? false,
          musicAnchor: at(cmd.position, same ? here : () => 0),
        });
      }
      if (cmd.type === 'full-sync') {
        useAudioStore.setState({
          activeLayers: (cmd.activeLayers ?? []).filter((id) => AMBIENCE_LAYERS.some((l) => l.id === id)),
          layerVolumes: cmd.layerVolumes ?? {},
        });
      }
      break;
    }
    case 'music-pause':
    case 'music-resume':
    case 'music-seek': {
      if (!state.currentTrack) break;
      useAudioStore.setState({
        musicAnchor: at(cmd.position, here),
        musicPaused: cmd.type === 'music-seek' ? state.musicPaused : cmd.type === 'music-pause',
      });
      break;
    }
    case 'layer-on': {
      if (cmd.layerId && !state.activeLayers.includes(cmd.layerId)) {
        useAudioStore.setState({
          activeLayers: [...state.activeLayers, cmd.layerId],
          layerVolumes: { ...state.layerVolumes, [cmd.layerId]: cmd.volume ?? 0.6 },
        });
      }
      break;
    }
    case 'layer-off': {
      useAudioStore.setState({ activeLayers: state.activeLayers.filter((id) => id !== cmd.layerId) });
      break;
    }
    case 'layer-vol': {
      if (cmd.layerId && cmd.volume !== undefined) {
        useAudioStore.setState({ layerVolumes: { ...state.layerVolumes, [cmd.layerId]: cmd.volume } });
      }
      break;
    }
  }
}

function drawNextTrack(moodId: string, lastId: string | null): MusicTrack | null {
  const draw = drawFromShuffleBag(moodPlaylist(moodId), shuffleBags.get(moodId) ?? [], lastId);
  if (!draw) return null;
  shuffleBags.set(moodId, draw.bag);
  return draw.track;
}

/**
 * Play a track on the GM's client. With more than one track in the mood the
 * engine warns before the end and we crossfade into the next pick; with one,
 * it is today's seamless loop.
 */
function startGmTrack(
  track: MusicTrack,
  fadeDuration: number,
  { recordHistory = true }: { recordHistory?: boolean } = {},
) {
  const hasNext = moodPlaylist(track.moodId).length > 1;
  void audioEngine.playMusic(track.url, {
    fadeDuration,
    endingLead: TRACK_END_CROSSFADE,
    onEnding: hasNext ? () => advanceAfter(track.id) : undefined,
  });
  if (recordHistory) {
    playHistory.set(track.moodId, pushHistory(playHistory.get(track.moodId) ?? [], track.id));
  }
  useAudioStore.setState({ currentMood: track.moodId, currentTrack: track, musicPaused: false });
}

function advanceAfter(endingTrackId: string) {
  const state = useAudioStore.getState();
  // The GM moved on by hand since this track started.
  if (state.currentTrack?.id !== endingTrackId || !state.currentMood) return;
  // Pinned: let the engine loop it.
  if (!shouldAdvance(state.repeatOne, moodPlaylist(state.currentMood).length)) return;

  const track = drawNextTrack(state.currentMood, endingTrackId);
  if (!track || track.id === endingTrackId) return;
  startGmTrack(track, TRACK_END_CROSSFADE);
  broadcast(useAudioStore.getState(), { type: 'mood', moodId: track.moodId, track });
}

/** A broadcast track is untrusted input: check the shape before playing it. */
function isMusicTrack(value: unknown): value is MusicTrack {
  if (!value || typeof value !== 'object') return false;
  const t = value as Record<string, unknown>;
  return (
    typeof t.id === 'string' &&
    typeof t.moodId === 'string' &&
    typeof t.title === 'string' &&
    typeof t.url === 'string' &&
    /^(https?:\/\/|\/)/.test(t.url)
  );
}

/**
 * Which track a 'mood' or 'full-sync' command means. A command from an older
 * GM client carries only moodId, so it gets that mood's first track.
 * Returns null for "stop" and undefined when nothing playable was named.
 */
export function trackFromCommand(cmd: AudioCommand): MusicTrack | null | undefined {
  if (isMusicTrack(cmd.track)) return cmd.track;
  if (cmd.moodId === null || (cmd.type === 'full-sync' && !cmd.moodId)) return null;
  if (!cmd.moodId) return undefined;
  return moodPlaylist(cmd.moodId)[0];
}

/**
 * Play a track the GM told us about. Always looped, never advanced: the next
 * pick is the GM's, and until it arrives the current track keeps going.
 * `position` lines a late or drifting player up with the GM.
 */
function playRemoteTrack(track: MusicTrack, position?: number, paused = false) {
  const { currentTrack } = useAudioStore.getState();
  if (currentTrack?.id === track.id && currentTrack.url === track.url) {
    if (position !== undefined) alignPosition(position);
    if (paused) audioEngine.pauseMusic(); else audioEngine.resumeMusic();
    useAudioStore.setState({ musicPaused: paused });
    return;
  }
  void audioEngine.playMusic(track.url, {
    fadeDuration: 2, loop: true, startAt: position, startPaused: paused,
  });
  useAudioStore.setState({ currentMood: track.moodId, currentTrack: track, musicPaused: paused });
}

/** Seek only when we are noticeably off the GM, so small drift does not stutter. */
function alignPosition(position: number) {
  if (!Number.isFinite(position)) return;
  const here = audioEngine.getMusicState()?.currentTime;
  if (here === undefined || Math.abs(here - position) > DRIFT_TOLERANCE) {
    audioEngine.seekMusic(position);
  }
}

/** Send an audio command to all players via Supabase broadcast */
function broadcast(state: AudioState, cmd: AudioCommand) {
  if (!state.isBroadcasting || !state.broadcastChannel) return;

  state.broadcastChannel.send({
    type: 'broadcast',
    event: 'audio-cmd',
    payload: cmd,
  });
}

/**
 * Handle a remote audio command. Players apply it to the audioEngine without
 * re-broadcasting; the GM only answers sync-requests.
 */
export function handleRemoteCommand(cmd: AudioCommand) {
  let state = useAudioStore.getState();

  if (cmd.type === 'sync-request') {
    // Only the GM answers, and never its own request.
    if (state.isBroadcasting && cmd.from !== AUDIO_CLIENT_ID) requestSyncReply();
    return;
  }
  // The GM is the authority: it does not take audio orders from the channel.
  if (state.isBroadcasting) return;

  // Without a user gesture an AudioContext starts suspended and plays nothing,
  // so hold the state until the player enables sound (init replays it).
  if (!state.isInitialized) {
    if (!hasUserActivation()) {
      rememberCommand(cmd);
      return;
    }
    state.init();
    state = useAudioStore.getState();
  }

  // Respect player mute — still update store state so unmuting restores correctly
  // but don't block the state updates (audio engine handles mute via master=0)

  switch (cmd.type) {
    case 'mood': {
      const track = trackFromCommand(cmd);
      if (track === null) {
        audioEngine.stopMusic();
        useAudioStore.setState({ currentMood: null, currentTrack: null, musicPaused: false });
      } else if (track) {
        playRemoteTrack(track, cmd.position, cmd.paused ?? false);
      }
      break;
    }
    case 'music-pause': {
      if (cmd.position !== undefined) alignPosition(cmd.position);
      audioEngine.pauseMusic();
      useAudioStore.setState({ musicPaused: true });
      break;
    }
    case 'music-resume': {
      if (cmd.position !== undefined) alignPosition(cmd.position);
      audioEngine.resumeMusic();
      useAudioStore.setState({ musicPaused: false });
      break;
    }
    case 'music-seek': {
      if (cmd.position !== undefined && Number.isFinite(cmd.position)) {
        audioEngine.seekMusic(cmd.position);
      }
      break;
    }
    case 'layer-on': {
      const layer = AMBIENCE_LAYERS.find((l) => l.id === cmd.layerId);
      if (layer && cmd.layerId && !state.activeLayers.includes(cmd.layerId)) {
        audioEngine.addAmbienceLayer(cmd.layerId, layer.url, cmd.volume ?? 0.6);
        useAudioStore.setState({
          activeLayers: [...state.activeLayers, cmd.layerId],
          layerVolumes: { ...state.layerVolumes, [cmd.layerId]: cmd.volume ?? 0.6 },
        });
      }
      break;
    }
    case 'layer-off': {
      if (cmd.layerId) {
        audioEngine.removeAmbienceLayer(cmd.layerId);
        useAudioStore.setState({
          activeLayers: state.activeLayers.filter((id) => id !== cmd.layerId),
        });
      }
      break;
    }
    case 'layer-vol': {
      if (cmd.layerId && cmd.volume !== undefined) {
        audioEngine.setAmbienceLayerVolume(cmd.layerId, cmd.volume);
        useAudioStore.setState({
          layerVolumes: { ...state.layerVolumes, [cmd.layerId]: cmd.volume },
        });
      }
      break;
    }
    case 'sfx': {
      const entry = SFX_ENTRIES.find((s) => s.id === cmd.sfxId);
      if (entry) {
        audioEngine.playSFX(entry.url);
      }
      break;
    }
    case 'full-sync': {
      // Idempotent: every player hears the GM's reply to someone else's join,
      // so a player already in step must not restart anything.
      const track = trackFromCommand(cmd) ?? null;
      if (track) {
        playRemoteTrack(track, cmd.position, cmd.paused ?? false);
      } else {
        if (state.currentTrack) audioEngine.stopMusic();
        useAudioStore.setState({ currentMood: null, currentTrack: null, musicPaused: false });
      }

      const layers = (cmd.activeLayers ?? []).filter((id) => AMBIENCE_LAYERS.some((l) => l.id === id));
      const vols = cmd.layerVolumes ?? {};
      for (const layerId of state.activeLayers) {
        if (!layers.includes(layerId)) audioEngine.removeAmbienceLayer(layerId);
      }
      for (const layerId of layers) {
        const volume = vols[layerId] ?? 0.6;
        if (state.activeLayers.includes(layerId)) {
          audioEngine.setAmbienceLayerVolume(layerId, volume);
        } else {
          const layer = AMBIENCE_LAYERS.find((l) => l.id === layerId)!;
          audioEngine.addAmbienceLayer(layerId, layer.url, volume);
        }
      }

      useAudioStore.setState({ activeLayers: layers, layerVolumes: vols });
      break;
    }
  }
}

// =============================================================================
// AUTO-INIT: initialize audio on first user interaction (click/keydown/touch)
// Browser autoplay policy requires a user gesture to create AudioContext.
// =============================================================================

function autoInitAudio() {
  const state = useAudioStore.getState();
  if (state.isInitialized) return;
  state.init();
  // Remove listeners after first init
  document.removeEventListener('click', autoInitAudio);
  document.removeEventListener('keydown', autoInitAudio);
  document.removeEventListener('touchstart', autoInitAudio);
}

if (typeof document !== 'undefined') {
  document.addEventListener('click', autoInitAudio, { once: true });
  document.addEventListener('keydown', autoInitAudio, { once: true });
  document.addEventListener('touchstart', autoInitAudio, { once: true });
}
